import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import Icon from "../components/Icon";
import BentoGallerySection from "../components/BentoGallerySection";
import CarouselGallerySection from "../components/CarouselGallerySection";
import MasonryGallerySection from "../components/MasonryGallerySection";
import FeaturedListGallerySection from "../components/FeaturedListGallerySection";
import products from "../data/products";
import "../styles/GalleryPage.css";

// products.js uses { name, image, category } — every layout section
// expects { title, image }, so map once here.
const toGalleryItem = (product) => ({
  id: product.id,
  title: product.name,
  image: product.image,
});

// Full-site Gallery page — the catalogue's 7 categories are grouped
// into 4 sections, each rendered with a different layout so the page
// doesn't read as one long repetitive grid.
const CATEGORY_GROUPS = [
  {
    key: "bridal",
    categories: ["Bridal & Wedding", "Sola & Pith Crafts"],
    eyebrow: "Browse The Collection",
    heading: "Bridal & Ceremonial",
    sub: "Bouquets and pieces crafted for weddings and special occasions.",
    Layout: BentoGallerySection,
  },
  {
    key: "wall-art",
    categories: ["Wall Hangings", "Areca Leaf Crafts"],
    eyebrow: "Browse The Collection",
    heading: "Wall Art & Areca Leaf",
    sub: "Statement pieces for your walls, hand-shaped from areca leaf and natural fibre.",
    Layout: CarouselGallerySection,
  },
  {
    key: "home-decor",
    categories: ["Home & Table Decor"],
    eyebrow: "Browse The Collection",
    heading: "Home & Table Decor",
    sub: "Centrepieces, bowls, and everyday pieces to bring nature indoors.",
    Layout: MasonryGallerySection,
  },
  {
    key: "shell-grain",
    categories: ["Shell & Nut Crafts", "Paddy & Grain Crafts"],
    eyebrow: "Browse The Collection",
    heading: "Shell, Nut & Grain Crafts",
    sub: "Textured pieces made from coconut shell, seed, and paddy waste.",
    Layout: FeaturedListGallerySection,
  },
];

export default function GalleryPage() {
  const groupedSections = useMemo(
    () =>
      CATEGORY_GROUPS.map((group) => ({
        ...group,
        items: products
          .filter((product) => group.categories.includes(product.category))
          .map(toGalleryItem),
      })).filter((group) => group.items.length > 0),
    []
  );

  return (
    <div className="tp-gallery-page">
      <section className="tp-gallery-page__hero tp-floral-bg2">
        <div className="tp-container">
          <span className="tp-eyebrow">Terra Bloom Studio</span>
          <h1 className="tp-section-heading">Our Full Gallery</h1>
          <p className="tp-section-sub">
            Every piece we've handcrafted from Kerala's natural waste — tap any photo for a
            closer look.
          </p>
        </div>
      </section>

      {groupedSections.map(({ key, items, eyebrow, heading, sub, Layout }) => (
        <Layout key={key} items={items} eyebrow={eyebrow} heading={heading} sub={sub} />
      ))}

      <section className="tp-gallery-page__cta">
        <div className="tp-container tp-gallery-page__cta-inner">
          <div>
            <h2>Like something you see?</h2>
            <p>Request a personalised quote for bulk orders, custom colours, or gifting sets.</p>
          </div>
          <Link to="/request-quote" className="tp-btn tp-btn--primary">
            Request a Quote
            <Icon name="arrow" size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
